import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ShoppingCart, User, LogOut, Shield, Package, Clock, CheckCircle, XCircle, AlertTriangle, Truck, MapPin, DollarSign, ExternalLink, Home, Gavel, Edit2, Save, X, Copy, Calendar, ChevronDown, ChevronUp, Zap, CreditCard, Handshake
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PayPalFnfButton from '../components/PayPalFnfButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { errMsg } from '../lib/utils';
import MyOffersSection from '../components/MyOffersSection';
import MyPaymentPlansSection from '../components/MyPaymentPlansSection';
import MySubmissionsSection from '../components/MySubmissionsSection';
import MyConsignmentsSection from '../components/MyConsignmentsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

// Valid US states for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington DC' }
];

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'PENDING PAYMENT' },
  pending_fulfillment: { icon: DollarSign, color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'PENDING FULFILLMENT' },
  pending_shipment: { icon: Package, color: 'text-tiffany-500', bg: 'bg-tiffany-500/10', label: 'PENDING SHIPMENT' },
  shipped: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'SHIPPED' },
  out_for_delivery: { icon: Truck, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'OUT FOR DELIVERY' },
  delivered: { icon: MapPin, color: 'text-green-500', bg: 'bg-green-500/10', label: 'DELIVERED' },
  cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'CANCELLED' },
  expired: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'EXPIRED' }
};

const CARRIER_TRACKING_URLS = {
  usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  ups: 'https://www.ups.com/track?tracknum=',
  fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
  dhl: 'https://www.dhl.com/en/express/tracking.html?AWB='
};

// Separate component for order card to handle local state
const OrderCard = ({ order, statusConfig, onUpgradeShipping }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const StatusIcon = statusConfig.icon;
  const itemCount = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const firstItem = order.items?.[0];
  
  const handleUpgradeShipping = async (e, tier = 'priority') => {
    e.stopPropagation();
    setUpgrading(true);
    try {
      await onUpgradeShipping(order.id, tier);
    } finally {
      setUpgrading(false);
    }
  };
  
  const copyTrackingNumber = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(order.tracking_number);
    toast.success('TRACKING NUMBER COPIED!');
  };
  
  return (
    <div 
      className="bg-zinc-900 border-2 border-zinc-700 rounded-sm overflow-hidden"
      data-testid={`order-${order.id}`}
    >
      {/* ALWAYS VISIBLE: Order Preview Header */}
      <div className="p-4">
        {/* Row 1: Order ID and Total */}
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-xl text-tiffany-500 font-bold">{order.order_id || order.id}</p>
          <div className="text-right">
            <p className="font-mono text-[10px] text-zinc-500 tracking-wider">TOTAL</p>
            <p className="font-mono text-xl text-white font-bold">${order.total?.toFixed(2)}</p>
          </div>
        </div>
        
        {/* Row 2: Date and Status Badge */}
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-xs text-zinc-500">
            {new Date(order.created_at).toLocaleDateString()}
          </p>
          <Badge 
            className={`${statusConfig.bg} ${statusConfig.color} border-0 rounded-sm flex items-center gap-2 px-3 py-1.5 text-xs font-bold`}
          >
            <StatusIcon className="w-4 h-4" />
            {statusConfig.label}
          </Badge>
        </div>
        
        {/* Row 3: First Item Preview */}
        {firstItem && (
          <div className="p-3 bg-zinc-800/50 rounded-sm mb-3">
            <p className="font-mono text-xs text-zinc-400 uppercase">
              {firstItem.brand && <span className="text-tiffany-500">{firstItem.brand} </span>}
              <span className="text-zinc-300">{firstItem.name}</span>
              {firstItem.variant_name && <span className="text-tiffany-500"> ({firstItem.variant_name})</span>}
              {order.items?.length > 1 && (
                <span className="text-zinc-500"> + {order.items.length - 1} MORE</span>
              )}
            </p>
          </div>
        )}

        {/* Trade-item hold banner: order is paid + waiting on trade item to ship */}
        {order.has_trade_item && order.awaiting_trade_item && order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' && (
          <div className="p-3 mb-3 bg-yellow-500/10 border border-yellow-500/40 rounded-sm" data-testid={`trade-hold-banner-${order.order_id || order.id}`}>
            <div className="flex items-start gap-2">
              <span className="text-yellow-400 font-mono text-xs font-bold">⏳ HOLD</span>
              <div className="min-w-0">
                <p className="font-mono text-xs text-yellow-400 font-bold leading-tight">
                  WAITING FOR YOUR TRADE ITEM
                </p>
                <p className="font-mono text-[11px] text-zinc-400 leading-snug mt-1">
                  We&apos;ll ship this order as soon as your trade item arrives. Make sure it&apos;s on its way!
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Trade item received — your order is moving */}
        {order.has_trade_item && !order.awaiting_trade_item && (order.status === 'pending_fulfillment' || order.status === 'paid') && (
          <div className="p-3 mb-3 bg-green-500/10 border border-green-500/40 rounded-sm">
            <p className="font-mono text-xs text-green-400 font-bold leading-tight">
              ✓ YOUR TRADE ITEM ARRIVED
            </p>
            <p className="font-mono text-[11px] text-zinc-400 leading-snug mt-1">
              Your order is now being prepared to ship.
            </p>
          </div>
        )}
        
        {/* Row 4: Tracking (if available) - Always visible for quick access */}
        {order.tracking_number && (
          <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-sm mb-3">
            <button
              onClick={copyTrackingNumber}
              className="flex-1 flex items-center gap-2 text-left hover:bg-zinc-700 p-1 -m-1 rounded transition-colors"
            >
              <Copy className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              <span className="font-mono text-sm text-white truncate">{order.tracking_number}</span>
            </button>
            <a
              href={`${CARRIER_TRACKING_URLS[order.shipping_carrier] || CARRIER_TRACKING_URLS.usps}${order.tracking_number}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-tiffany-500 text-black font-mono text-xs px-4 py-2 hover:bg-tiffany-400 transition-colors font-bold rounded-sm flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4" />
              TRACK
            </a>
          </div>
        )}
        
        {/* Row 5: Expand Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-sm transition-colors"
        >
          <span className="font-mono text-xs text-zinc-400">
            {itemCount} ITEM{itemCount !== 1 ? 'S' : ''}
          </span>
          <div className="flex items-center gap-2 text-zinc-400">
            <span className="font-mono text-xs">{isExpanded ? 'HIDE DETAILS' : 'VIEW DETAILS'}</span>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>
      </div>
      
      {/* EXPANDABLE: Order Details */}
      {isExpanded && (
        <div className="border-t border-zinc-700">
          {/* Order Items */}
          <div className="p-4 space-y-3">
            <p className="font-mono text-[10px] text-zinc-500 tracking-wider mb-2">ORDER ITEMS</p>
            {order.items?.map((item, idx) => (
              <div key={`${item.product_id}-${idx}`} className="flex justify-between items-start text-sm py-2 border-b border-zinc-800 last:border-0">
                <div className="flex-1">
                  {item.brand && (
                    <span className="font-mono text-tiffany-500 text-[10px] uppercase tracking-wider block">{item.brand}</span>
                  )}
                  <span className="font-mono text-zinc-300 uppercase">{item.name}</span>
                  {item.variant_name && (
                    <span className="font-mono text-tiffany-500 text-xs ml-1 uppercase">({item.variant_name})</span>
                  )}
                  <span className="font-mono text-zinc-600 text-xs ml-2">×{item.quantity}</span>
                </div>
                <span className="font-mono text-zinc-400">${item.item_total?.toFixed(2)}</span>
              </div>
            ))}
            {order.shipping_cost > 0 && (
              <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                <span className="font-mono text-zinc-500 uppercase">SHIPPING</span>
                <span className="font-mono text-zinc-500">${order.shipping_cost?.toFixed(2)}</span>
              </div>
            )}
            {/* Subtotal — only show when there are discounts/credits to break down */}
            {(order.blkcrd_discount > 0 || order.discount_amount > 0 || order.gift_card_amount > 0) && order.subtotal > 0 && (
              <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                <span className="font-mono text-zinc-500 uppercase">SUBTOTAL</span>
                <span className="font-mono text-zinc-500">${order.subtotal?.toFixed(2)}</span>
              </div>
            )}
            {/* BLKCRD member discount (15% off eligible items) */}
            {order.blkcrd_discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="font-mono text-tiffany-500 uppercase flex items-center gap-1">
                  <span className="text-[10px] bg-tiffany-500 text-black px-1.5 py-0.5 font-bold">BLKCRD</span>
                  MEMBER DISCOUNT (15%)
                </span>
                <span className="font-mono text-tiffany-500">−${order.blkcrd_discount?.toFixed(2)}</span>
              </div>
            )}
            {/* Promo code discount */}
            {order.discount_amount > 0 && order.promo_code && (
              <div className="flex justify-between text-sm">
                <span className="font-mono text-green-400 uppercase">
                  PROMO {order.promo_code}
                </span>
                <span className="font-mono text-green-400">−${order.discount_amount?.toFixed(2)}</span>
              </div>
            )}
            {/* Gift card applied */}
            {order.gift_card_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="font-mono text-amber-400 uppercase flex items-center gap-1">
                  <span className="text-[10px] bg-amber-500 text-black px-1.5 py-0.5 font-bold">GIFT</span>
                  {order.gift_card_code || 'GIFT CARD'}
                </span>
                <span className="font-mono text-amber-400">−${order.gift_card_amount?.toFixed(2)}</span>
              </div>
            )}
            {/* Order Total at bottom of items */}
            <div className="flex justify-between text-sm pt-3 mt-2 border-t border-zinc-700">
              <span className="font-mono text-white font-bold">ORDER TOTAL</span>
              <span className="font-mono text-white font-bold text-lg">${order.total?.toFixed(2)}</span>
            </div>
          </div>

          {/* Pending Payment Notice */}
          {order.status === 'pending' && (
            <div className="p-4 bg-yellow-500/10 border-t border-zinc-700">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="font-mono text-xs uppercase">
                  <p className="text-yellow-500 font-bold mb-2">AWAITING PAYMENT</p>
                  <p className="text-zinc-400 mb-1">SEND PAYMENT VIA <span className="text-white">PAYPAL FRIENDS & FAMILY</span> TO:</p>
                  <p className="text-tiffany-500">PAYMENTS@SHOPBLKMRKT.COM</p>
                  <p className="text-tiffany-500">@BLKMRKTFB <span className="text-zinc-500">(RYAN GRENIER)</span></p>
                  <p className="mt-2 text-zinc-400">INCLUDE ORDER ID <span className="text-tiffany-500">{order.order_id || order.id}</span> IN THE NOTES.</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Shipping Upgrade Options — only for pending orders that haven't upgraded yet */}
          {order.status === 'pending' && !order.priority_shipping && !order.overnight_shipping && (
            <div className="p-4 border-t border-zinc-700 space-y-2">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-zinc-500" />
                <span className="font-mono text-xs text-zinc-400">STANDARD SHIPPING (FREE)</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={(e) => handleUpgradeShipping(e, 'priority')}
                  disabled={upgrading}
                  className="flex-1 min-w-[180px] flex items-center justify-center gap-2 bg-tiffany-500 hover:bg-tiffany-400 text-black font-mono text-xs px-3 py-2 transition-colors disabled:opacity-50"
                  data-testid={`upgrade-priority-${order.id}`}
                >
                  <Zap className="w-4 h-4" />
                  {upgrading ? 'UPGRADING...' : 'UPGRADE TO PRIORITY +$15'}
                </button>
                <button
                  onClick={(e) => handleUpgradeShipping(e, 'overnight')}
                  disabled={upgrading}
                  className="flex-1 min-w-[180px] flex items-center justify-center gap-2 border border-tiffany-500 text-tiffany-500 hover:bg-tiffany-500/10 font-mono text-xs px-3 py-2 transition-colors disabled:opacity-50"
                  data-testid={`upgrade-overnight-${order.id}`}
                >
                  <Zap className="w-4 h-4" />
                  {upgrading ? 'UPGRADING...' : 'UPGRADE TO OVERNIGHT +$50'}
                </button>
              </div>
            </div>
          )}

          {/* Already on Priority — offer overnight upgrade for the delta */}
          {order.status === 'pending' && order.priority_shipping && !order.overnight_shipping && (
            <div className="p-4 border-t border-zinc-700">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-tiffany-500" />
                  <span className="font-mono text-xs text-tiffany-500">PRIORITY SHIPPING ($15.00)</span>
                  <span className="font-mono text-[10px] text-green-500 bg-green-500/20 px-2 py-0.5">UPGRADED</span>
                </div>
                <button
                  onClick={(e) => handleUpgradeShipping(e, 'overnight')}
                  disabled={upgrading}
                  className="flex items-center gap-2 border border-tiffany-500 text-tiffany-500 hover:bg-tiffany-500/10 font-mono text-xs px-3 py-2 transition-colors disabled:opacity-50"
                  data-testid={`upgrade-overnight-from-priority-${order.id}`}
                >
                  <Zap className="w-4 h-4" />
                  {upgrading ? 'UPGRADING...' : 'UPGRADE TO OVERNIGHT +$35'}
                </button>
              </div>
            </div>
          )}

          {/* Overnight Shipping Badge */}
          {order.status === 'pending' && order.overnight_shipping && (
            <div className="p-4 border-t border-zinc-700">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-tiffany-500" />
                <span className="font-mono text-xs text-tiffany-500">OVERNIGHT SHIPPING ($25.00)</span>
                <span className="font-mono text-[10px] text-green-500 bg-green-500/20 px-2 py-0.5 ml-auto">UPGRADED</span>
              </div>
            </div>
          )}

          {/* PayPal Transaction ID (if confirmed) */}
          {order.paypal_transaction_id && (
            <div className="px-4 py-3 border-t border-zinc-700">
              <p className="font-mono text-[10px] text-zinc-500 tracking-wider">PAYPAL TRANSACTION</p>
              <p className="font-mono text-sm text-zinc-400">{order.paypal_transaction_id}</p>
            </div>
          )}

          {/* Shipping Info */}
          {order.tracking_number && (
            <div className="px-4 py-3 border-t border-zinc-700">
              <p className="font-mono text-[10px] text-zinc-500 tracking-wider">SHIPPING</p>
              <p className="font-mono text-xs text-zinc-400 mt-1">
                {order.shipping_carrier?.toUpperCase() || 'USPS'} • {order.shipping_method?.replace(/_/g, ' ').toUpperCase() || 'GROUND'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user, userProfile, logout, getAuthHeader, refreshProfile } = useAuth();
  const { itemCount } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [activeBids, setActiveBids] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Profile editing state - auto-open if ?edit=address in URL
  const [isEditingProfile, setIsEditingProfile] = useState(searchParams.get('edit') === 'address');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    email: '',
    full_name: '',
    country: '',
    home_country: '',
    address: '',
    apt_unit: '',
    city: '',
    state: '',
    zip_code: ''
  });
  
  // BLKCRD membership state
  const [blkcrdStatus, setBlkcrdStatus] = useState(null);
  const [updatingAutoRenew, setUpdatingAutoRenew] = useState(false);

  // Countries list for dropdown - PayPal G&S + Forwarding supported
  const COUNTRIES = [
    { code: 'US', name: 'United States' },
    { code: 'KR', name: 'South Korea' },
    { code: 'JP', name: 'Japan' },
    { code: 'CN', name: 'China' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'SG', name: 'Singapore' },
    { code: 'PH', name: 'Philippines' },
    { code: 'TH', name: 'Thailand' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'IN', name: 'India' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'NP', name: 'Nepal' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'AU', name: 'Australia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'AT', name: 'Austria' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'IE', name: 'Ireland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IS', name: 'Iceland' },
    { code: 'PL', name: 'Poland' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'HU', name: 'Hungary' },
    { code: 'RO', name: 'Romania' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'RS', name: 'Serbia' },
    { code: 'GR', name: 'Greece' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LV', name: 'Latvia' },
    { code: 'EE', name: 'Estonia' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'TR', name: 'Turkey' },
    { code: 'GE', name: 'Georgia' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'IL', name: 'Israel' },
    { code: 'QA', name: 'Qatar' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'OM', name: 'Oman' },
    { code: 'JO', name: 'Jordan' },
    { code: 'EG', name: 'Egypt' },
    { code: 'CA', name: 'Canada' },
    { code: 'MX', name: 'Mexico' },
    { code: 'BR', name: 'Brazil' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'PE', name: 'Peru' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'PA', name: 'Panama' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'MA', name: 'Morocco' },
    { code: 'KE', name: 'Kenya' },
    { code: 'GH', name: 'Ghana' },
  ];

  const getCountryName = (code) => {
    const country = COUNTRIES.find(c => c.code === code);
    return country ? country.name : code || 'Not set';
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Clear URL param after opening edit mode
  useEffect(() => {
    if (searchParams.get('edit') === 'address') {
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Initialize profile form when userProfile loads
  useEffect(() => {
    if (user && userProfile) {
      setProfileForm({
        email: user.email || '',
        full_name: userProfile.full_name || '',
        country: userProfile.country || 'US',
        home_country: userProfile.home_country || '',
        address: userProfile.address || '',
        apt_unit: userProfile.apt_unit || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        zip_code: userProfile.zip_code || ''
      });
    }
  }, [user, userProfile]);

  const fetchData = async () => {
    try {
      const [ordersRes, bidsRes, blkcrdRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/user/active-bids`, { headers: getAuthHeader() }).catch(() => ({ data: { bids: [] } })),
        axios.get(`${API}/blkcrd/status`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      setOrders(ordersRes.data);
      setActiveBids(bidsRes.data.bids || []);
      setBlkcrdStatus(blkcrdRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    if (!blkcrdStatus) return;
    try {
      setUpdatingAutoRenew(true);
      const newValue = !blkcrdStatus.auto_renew;
      await axios.put(`${API}/blkcrd/auto-renew`, { auto_renew: newValue }, { headers: getAuthHeader() });
      setBlkcrdStatus(prev => ({ ...prev, auto_renew: newValue }));
      toast.success(`Auto-renew ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update auto-renew setting');
    } finally {
      setUpdatingAutoRenew(false);
    }
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      
      const response = await axios.put(
        `${API}/user/profile`,
        profileForm,
        { headers: getAuthHeader() }
      );
      
      // If email changed, force logout and redirect to login
      if (response.data.force_logout) {
        toast.success('Email changed! Please verify your new email and log in again.');
        logout();
        navigate('/login');
        return;
      }
      
      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
      // Refresh profile data
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form to original values
    if (user && userProfile) {
      setProfileForm({
        email: user.email || '',
        full_name: userProfile.full_name || '',
        country: userProfile.country || 'US',
        home_country: userProfile.home_country || '',
        address: userProfile.address || '',
        apt_unit: userProfile.apt_unit || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        zip_code: userProfile.zip_code || ''
      });
    }
    setIsEditingProfile(false);
  };

  const handleUpgradeShipping = async (orderId, tier = 'priority') => {
    try {
      const endpoint = tier === 'overnight'
        ? `${API}/orders/${orderId}/upgrade-shipping-overnight`
        : `${API}/orders/${orderId}/upgrade-shipping`;
      const response = await axios.post(endpoint, {}, { headers: getAuthHeader() });
      toast.success(response.data.message || (tier === 'overnight'
        ? 'SHIPPING UPGRADED TO OVERNIGHT!'
        : 'SHIPPING UPGRADED TO PRIORITY!'));
      // Refresh orders to show updated total and shipping status
      await fetchData();
    } catch (error) {
      toast.error(errMsg(error, 'FAILED TO UPGRADE SHIPPING'));
      throw error;
    }
  };

  const displayName = userProfile?.full_name || user?.full_name || user?.email || 'User';

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm" style={{ top: 'var(--banner-offset, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link 
              to="/catalog"
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="home-link"
            >
              <Home className="w-5 h-5" />
            </Link>
            <Link 
              to="/cart" 
              className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link 
              to="/dashboard" 
              className="p-2 text-tiffany-500"
              data-testid="dashboard-link"
            >
              <User className="w-5 h-5" />
            </Link>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                data-testid="admin-link"
              >
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <button 
              onClick={logout}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        {/* Pending Orders Alert - Shows at top if user has pending orders */}
        {orders.filter(o => o.status === 'pending').length > 0 && (
          <div className="mb-6 border-2 border-amber-500 bg-amber-500/10 p-4 sm:p-6" data-testid="pending-orders-alert">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 animate-pulse" />
              <div>
                <h2 className="font-mono text-lg text-amber-500 font-bold">
                  {orders.filter(o => o.status === 'pending').length} ORDER{orders.filter(o => o.status === 'pending').length > 1 ? 'S' : ''} AWAITING PAYMENT
                </h2>
                <p className="font-mono text-sm text-zinc-400 mt-1">
                  Orders expire 30 minutes after placement if payment is not received
                </p>
              </div>
            </div>
            
            {/* PAY NOW — total of all pending orders, with F&F confirmation */}
            {(() => {
              const pendingTotal = orders
                .filter(o => o.status === 'pending')
                .reduce((sum, o) => sum + (o.total || 0), 0);
              return pendingTotal > 0 ? (
                <div className="mb-4">
                  <PayPalFnfButton
                    amount={pendingTotal}
                    testId="dashboard-paypal-pay-now-btn"
                  />
                  <p className="font-mono text-[11px] text-zinc-500 text-center mt-2">
                    Select <span className="text-white font-bold">"SENDING TO A FRIEND"</span> on PayPal to avoid fees
                  </p>
                </div>
              ) : null;
            })()}
            
            {/* Manual payment details — quiet, collapsible feel */}
            <details className="bg-black/30 p-4 rounded mb-4">
              <summary className="cursor-pointer list-none font-mono text-xs text-zinc-500 tracking-widest flex items-center justify-between">
                <span>SEND MANUALLY</span>
                <span>+</span>
              </summary>
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <p className="font-mono text-xs text-zinc-500 mb-1">PAYPAL F&amp;F TO</p>
                  <p className="font-mono text-sm text-tiffany-500">payments@shopblkmrkt.com</p>
                  <p className="font-mono text-xs text-zinc-400 mt-1">@BLKMRKTFB (Ryan Grenier)</p>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('payments@shopblkmrkt.com');
                    toast.success('PayPal email copied!');
                  }}
                  className="flex items-center gap-2 bg-tiffany-500 text-black px-4 py-2 font-mono text-xs font-bold hover:bg-tiffany-400 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  COPY EMAIL
                </button>
              </div>
            </details>
            
            {/* Pending Order List */}
            <div className="space-y-2">
              {orders.filter(o => o.status === 'pending').map(order => (
                <div key={order.id} className="flex items-center justify-between bg-black/20 p-3 rounded">
                  <div>
                    <span className="font-mono text-sm text-white">{order.id}</span>
                    <span className="font-mono text-xs text-zinc-500 ml-2">
                      {new Date(order.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                  <span className="font-mono text-lg text-tiffany-500 font-bold">${order.total.toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            {/* Trade Ship-To Address — show only if any pending order contains a trade item */}
            {orders.filter(o => o.status === 'pending').some(o => (o.items || []).some(it => it.is_trade)) && (
              <div className="mt-4 pt-4 border-t border-tiffany-500/30">
                <p className="font-mono text-xs text-tiffany-500 font-bold mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  SHIP YOUR TRADE-IN TO
                </p>
                <div className="bg-black/30 p-3 rounded">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-mono text-white text-sm leading-relaxed">
                      <p className="text-tiffany-500 font-bold">BLK MRKT</p>
                      <p>PO BOX 26</p>
                      <p>SACO, ME 04072</p>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('BLK MRKT\nPO BOX 26\nSACO, ME 04072');
                        toast.success('Address copied!');
                      }}
                      className="flex items-center gap-1.5 bg-tiffany-500 text-black px-3 py-1.5 font-mono text-[10px] font-bold hover:bg-tiffany-400 transition-colors flex-shrink-0"
                    >
                      <Copy className="w-3 h-3" />
                      COPY
                    </button>
                  </div>
                </div>
                <p className="font-mono text-[10px] text-zinc-500 mt-2">
                  TIP: WRITE YOUR ORDER ID ON THE PACKAGE OR INSIDE.
                </p>
              </div>
            )}
            
            {/* Warning */}
            <div className="mt-4 pt-4 border-t border-amber-500/30 flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-red-400">
                <span className="font-bold">DO NOT</span> use "Goods & Services" option. Use <span className="text-white">Friends & Family</span> only or your order will not be processed.
              </p>
            </div>
          </div>
        )}
        
        {/* User Info */}
        <div className="border border-zinc-800 p-4 sm:p-6 mb-8">
          {/* Header row with avatar, name, and edit button */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 sm:w-8 sm:h-8 text-tiffany-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-sans text-lg sm:text-2xl text-white truncate" data-testid="user-welcome">
                  {displayName.toUpperCase()}
                </h1>
                {blkcrdStatus?.status === 'active' && (
                  <span className="bg-purple-500/20 text-purple-400 font-mono text-[10px] px-2 py-0.5 border border-purple-500/30">
                    BLKCRD
                  </span>
                )}
              </div>
              <p className="font-mono text-xs sm:text-sm text-zinc-500 truncate">{user?.email}</p>
            </div>
            {!isEditingProfile && (
              <Button
                onClick={() => setIsEditingProfile(true)}
                variant="outline"
                size="sm"
                className="rounded-none border-zinc-700 text-zinc-400 hover:text-tiffany-500 hover:border-tiffany-500 flex-shrink-0 px-2 sm:px-3"
                data-testid="edit-profile-btn"
              >
                <Edit2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">EDIT</span>
              </Button>
            )}
          </div>

          {isEditingProfile ? (
            /* Edit Profile Form */
            <div className="border-t border-zinc-800 pt-4 mt-4 space-y-3">
              <h3 className="font-mono text-xs text-tiffany-500 mb-2">EDIT PROFILE</h3>
              
              {/* Email & Name - stack on mobile */}
              <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-3">
                <div>
                  <label className="block font-mono text-xs text-zinc-500 mb-1">EMAIL</label>
                  <Input
                    name="email"
                    type="email"
                    value={profileForm.email}
                    onChange={handleProfileChange}
                    className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500"
                    data-testid="edit-email-input"
                  />
                  <p className="font-mono text-[10px] text-zinc-600 mt-1">Changing email will require re-verification</p>
                </div>
                <div>
                  <label className="block font-mono text-xs text-zinc-500 mb-1">FULL NAME</label>
                  <Input
                    name="full_name"
                    value={profileForm.full_name}
                    onChange={handleProfileChange}
                    className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500"
                    data-testid="edit-name-input"
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">COUNTRY</label>
                <Select
                  value={profileForm.country || 'US'}
                  onValueChange={(value) => handleProfileChange({ target: { name: 'country', value } })}
                >
                  <SelectTrigger className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500" data-testid="edit-country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-white font-mono max-h-60">
                    {COUNTRIES.map(country => (
                      <SelectItem key={country.code} value={country.code} className="text-white hover:bg-zinc-700 focus:bg-zinc-700 focus:text-white cursor-pointer">
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {profileForm.country && profileForm.country !== 'US' && (
                  <p className="font-mono text-[10px] text-purple-400 mt-1">INTERNATIONAL - USE US FORWARDING SERVICE ADDRESS</p>
                )}
                {/* Show home country if set (international user with forwarding) */}
                {userProfile?.home_country && (
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">
                    HOME COUNTRY: {getCountryName(userProfile.home_country)}
                  </p>
                )}
              </div>

              {/* Street Address - full width */}
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">STREET ADDRESS</label>
                <Input
                  name="address"
                  value={profileForm.address}
                  onChange={handleProfileChange}
                  className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500"
                  data-testid="edit-address-input"
                />
              </div>

              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">APT / UNIT / SUITE</label>
                <Input
                  name="apt_unit"
                  value={profileForm.apt_unit}
                  onChange={handleProfileChange}
                  placeholder="Optional"
                  className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500 placeholder:text-zinc-600"
                  data-testid="edit-apt-unit-input"
                />
              </div>

              {/* City/State/Zip - stack on mobile, 3-col on tablet+ */}
              <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-3">
                <div>
                  <label className="block font-mono text-xs text-zinc-500 mb-1">CITY</label>
                  <Input
                    name="city"
                    value={profileForm.city}
                    onChange={handleProfileChange}
                    className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500"
                    data-testid="edit-city-input"
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs text-zinc-500 mb-1">STATE</label>
                  <Select
                    value={profileForm.state?.toUpperCase() || ''}
                    onValueChange={(value) => setProfileForm(prev => ({ ...prev, state: value }))}
                  >
                    <SelectTrigger 
                      className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none uppercase"
                      data-testid="edit-state-input"
                    >
                      <SelectValue placeholder="SELECT STATE">
                        {profileForm.state ? `${profileForm.state.toUpperCase()} - ${US_STATES.find(s => s.code === profileForm.state?.toUpperCase())?.name?.toUpperCase() || ''}` : 'SELECT STATE'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 max-h-48 overflow-y-auto">
                      {US_STATES.map(state => (
                        <SelectItem 
                          key={state.code} 
                          value={state.code}
                          className="text-white font-mono text-sm hover:bg-zinc-800 py-2 uppercase"
                        >
                          {state.code} - {state.name.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block font-mono text-xs text-zinc-500 mb-1">ZIP</label>
                  <Input
                    name="zip_code"
                    value={profileForm.zip_code}
                    onChange={handleProfileChange}
                    className="h-9 w-full bg-zinc-900 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500"
                    data-testid="edit-zip-input"
                  />
                </div>
              </div>

              {/* Action buttons - stack on very small screens */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="w-full sm:w-auto bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none font-mono text-xs"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingProfile ? 'SAVING...' : 'SAVE CHANGES'}
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  className="w-full sm:w-auto rounded-none border-zinc-700 text-zinc-400 hover:text-white font-mono text-xs"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  CANCEL
                </Button>
              </div>
            </div>
          ) : (
            /* Display Profile Info */
            userProfile && (
              <div className="border-t border-zinc-800 pt-4 mt-4">
                <div className="mb-3">
                  <p className="font-mono text-xs text-zinc-500 mb-1">COUNTRY</p>
                  <p className="font-mono text-sm text-zinc-400 uppercase">
                    {getCountryName(userProfile.country)}
                    {userProfile.country && userProfile.country !== 'US' && (
                      <span className="text-purple-400 ml-2">(INTERNATIONAL)</span>
                    )}
                  </p>
                </div>
                <p className="font-mono text-xs text-zinc-500 mb-1">SHIPPING ADDRESS</p>
                <p className="font-mono text-sm text-zinc-400 uppercase">
                  {userProfile.address}{userProfile.apt_unit ? `, ${userProfile.apt_unit}` : ''}<br />
                  {userProfile.city}, {userProfile.state} {userProfile.zip_code}
                </p>
              </div>
            )
          )}
        </div>

        {/* BLKCRD Membership Section */}
        {blkcrdStatus && (blkcrdStatus.status === 'active' || blkcrdStatus.status === 'expired') && (
          <div className={`border p-4 sm:p-6 mb-8 ${blkcrdStatus.status === 'active' ? 'border-purple-500/50 bg-purple-500/5' : 'border-zinc-800 bg-zinc-900/50'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center ${blkcrdStatus.status === 'active' ? 'bg-purple-500/20' : 'bg-zinc-800'}`}>
                  <CreditCard className={`w-5 h-5 ${blkcrdStatus.status === 'active' ? 'text-purple-400' : 'text-zinc-500'}`} />
                </div>
                <div>
                  <h2 className="font-mono text-sm text-white">BLKCRD MEMBERSHIP</h2>
                  <span className={`font-mono text-xs ${blkcrdStatus.status === 'active' ? 'text-purple-400' : 'text-red-400'}`}>
                    {blkcrdStatus.status === 'active' ? 'ACTIVE' : 'EXPIRED'}
                  </span>
                </div>
              </div>
              {blkcrdStatus.status === 'active' && (
                <span className="bg-purple-500/20 text-purple-400 font-mono text-[10px] px-2 py-1 border border-purple-500/30">
                  -15% ALL ORDERS
                </span>
              )}
            </div>
            
            {blkcrdStatus.status === 'active' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono text-zinc-500">EXPIRES</span>
                  <span className="font-mono text-white">
                    {new Date(blkcrdStatus.expires_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    }).toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-zinc-500 text-sm">AUTO-RENEW</span>
                  <Button
                    onClick={handleToggleAutoRenew}
                    disabled={updatingAutoRenew}
                    variant="outline"
                    size="sm"
                    className={`rounded-none font-mono text-xs ${
                      blkcrdStatus.auto_renew 
                        ? 'border-purple-500 text-purple-400 hover:bg-purple-500/10' 
                        : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
                    }`}
                  >
                    {updatingAutoRenew ? '...' : blkcrdStatus.auto_renew ? 'ON' : 'OFF'}
                  </Button>
                </div>
                <div className="border-t border-zinc-800 pt-4">
                  <p className="font-mono text-[10px] text-zinc-600 uppercase">
                    MEMBER BENEFITS: 15% OFF ALL PURCHASES • EARLY ACCESS TO COMING SOON ITEMS
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="font-mono text-sm text-zinc-400 uppercase">
                  YOUR MEMBERSHIP HAS EXPIRED. RENEW TO CONTINUE ENJOYING 15% OFF AND EXCLUSIVE ACCESS.
                </p>
                <Link
                  to="/product/80dfc239-ad34-4cd1-a0b1-bd6823afc638"
                  className="block bg-purple-500 text-black font-mono text-sm py-3 px-4 text-center hover:bg-purple-400 transition-colors"
                >
                  RENEW MEMBERSHIP
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Active Bids Section */}
        {activeBids.length > 0 && (
          <div className="mb-8">
            <h2 className="font-sans text-xl text-white mb-4 flex items-center gap-2">
              <Gavel className="w-5 h-5 text-tiffany-500" />
              MY ACTIVE BIDS
            </h2>
            <div className="space-y-3">
              {activeBids.map((bid) => (
                <Link 
                  key={bid.id}
                  to={`/product/${bid.product_id}`}
                  className={`block border p-4 transition-colors ${
                    bid.is_winning 
                      ? 'border-tiffany-500/50 bg-tiffany-500/5 hover:border-tiffany-500' 
                      : 'border-red-500/50 bg-red-500/5 hover:border-red-500'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {bid.product_image && (
                      <img src={bid.product_image} alt={bid.product_name} className="w-16 h-16 object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-white truncate">{bid.product_name}</span>
                        {bid.is_winning ? (
                          <Badge className="bg-tiffany-500 text-black rounded-none border-0 text-xs">
                            WINNING
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500 text-white rounded-none border-0 text-xs">
                            OUTBID
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-mono text-zinc-500">
                          Your bid: <span className="text-white">${bid.amount?.toFixed(2)}</span>
                        </span>
                        <span className="font-mono text-zinc-500">
                          Current high: <span className={bid.is_winning ? 'text-tiffany-500' : 'text-red-500'}>
                            ${bid.current_high_bid?.toFixed(2)}
                          </span>
                        </span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-zinc-500" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* My Offers Section */}
        <div className="mb-8">
          <h2 className="font-sans text-xl text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-yellow-500" />
            MY OFFERS
          </h2>
          <MyOffersSection 
            token={localStorage.getItem('blkmrkt_token')} 
            onCartUpdated={() => {
              // Refresh cart count if needed
            }}
          />
        </div>

        {/* My Payment Plans Section */}
        <div className="mb-8">
          <h2 className="font-sans text-xl text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-tiffany-500" />
            MY PAYMENT PLANS
          </h2>
          <MyPaymentPlansSection />
        </div>

        {/* Consignment Section */}
        {!userProfile?.is_international && (
          <div className="mb-8">
            <MyConsignmentsSection />
          </div>
        )}

        {/* Submissions Section - Hidden for international users */}
        {!userProfile?.is_international && (
          <div className="mb-8">
            <h2 className="font-sans text-xl text-white mb-6 flex items-center gap-2">
              MY SUBMISSIONS
            </h2>
            <MySubmissionsSection isInternational={userProfile?.is_international} />
          </div>
        )}

        {/* Orders Section */}
        <h2 className="font-sans text-xl text-white mb-6">MY ORDERS</h2>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-tiffany-500 font-mono cursor-blink">LOADING</div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 border border-zinc-800">
            <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="font-mono text-zinc-500 mb-4">NO ORDERS YET</p>
            <Link 
              to="/catalog"
              className="font-mono text-tiffany-500 hover:underline"
              data-testid="shop-link"
            >
              START SHOPPING
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-8" data-testid="orders-list">
            {orders.map((order) => {
              const statusConfig = getStatusConfig(order.status);
              return (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  statusConfig={statusConfig}
                  onUpgradeShipping={handleUpgradeShipping}
                />
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
